import { useCallback, useMemo } from 'react'
import { useMutation } from '@tanstack/react-query'
import { submitMessageFeedback } from 'tripPlanner/api'
import type { MessageFeedbackReaction } from 'tripPlanner/api/types'

interface SubmitFeedbackParams {
  chatId: number
  messageId: string
  reaction: MessageFeedbackReaction
}

interface UseFeedbackArgs {
  chatId?: number
  lastAssistantMessageId?: string
  existingFeedback?: Record<string, MessageFeedbackReaction>
  onFeedbackSubmitted?: (messageId: string, reaction: MessageFeedbackReaction) => void
}

export function useFeedback({
  chatId,
  lastAssistantMessageId,
  existingFeedback = {},
  onFeedbackSubmitted,
}: UseFeedbackArgs) {
  const feedbackSubmitted = useMemo(() => {
    if (!lastAssistantMessageId) {
      return undefined
    }
    return existingFeedback[lastAssistantMessageId]
  }, [lastAssistantMessageId, existingFeedback])

  const mutation = useMutation({
    mutationFn: async({ chatId, messageId, reaction }: SubmitFeedbackParams) => {
      return submitMessageFeedback(chatId, messageId, reaction)
    },
    onSuccess: (_data, variables) => {
      onFeedbackSubmitted?.(variables.messageId, variables.reaction)
    },
  })

  const handleFeedback = useCallback((reaction: MessageFeedbackReaction) => {
    if (!chatId || !lastAssistantMessageId || mutation.isPending || feedbackSubmitted) {
      return
    }

    mutation.mutate({
      chatId,
      messageId: lastAssistantMessageId,
      reaction,
    })
  }, [chatId, lastAssistantMessageId, mutation, feedbackSubmitted])

  const handleThumbsUp = useCallback(() => {
    handleFeedback('UP')
  }, [handleFeedback])

  const handleThumbsDown = useCallback(() => {
    handleFeedback('DOWN')
  }, [handleFeedback])

  return {
    feedbackSubmitted,
    isSubmitting: mutation.isPending,
    handleThumbsUp,
    handleThumbsDown,
  }
}
